export type SwitchOverlayKind = 'locale' | 'theme' | 'page' | 'boot' | 'auth' | 'generic';

export type SwitchOverlayStep = {
  key: string;
  label: string;
};

type SwitchOverlayState = {
  open: boolean;
  kind: SwitchOverlayKind;
  title: string;
  subtitle: string;
  progress: number;
  steps: SwitchOverlayStep[];
  activeStep: number;
};

const MIN_MS = 720;
const MAX_MS = 1100;
const PAGE_MIN_MS = 520;
const BOOT_MIN_MS = 980;
const AUTH_MIN_MS = 1280;

export function useAppSwitchOverlay() {
  const state = useState<SwitchOverlayState>('app-switch-overlay', () => ({
    open: false,
    kind: 'generic',
    title: '',
    subtitle: '',
    progress: 0,
    steps: [],
    activeStep: 0,
  }));

  let timer: ReturnType<typeof setInterval> | null = null;
  let resolveLock: (() => void) | null = null;
  let pageStartedAt = 0;
  let pageMode = false;
  let pageMinMs = PAGE_MIN_MS;
  let pageEndTimer: ReturnType<typeof setTimeout> | null = null;
  let pageFailsafeTimer: ReturnType<typeof setTimeout> | null = null;

  function clearTimer() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function clearFailsafe() {
    if (pageFailsafeTimer) {
      clearTimeout(pageFailsafeTimer);
      pageFailsafeTimer = null;
    }
  }

  function syncStepFromProgress(progress: number, stepsLen: number) {
    if (stepsLen <= 0) {
      state.value.activeStep = 0;
      return;
    }
    const idx = Math.min(stepsLen - 1, Math.floor((progress / 100) * stepsLen));
    state.value.activeStep = idx;
    const step = state.value.steps[idx];
    if (step?.label) state.value.subtitle = step.label;
  }

  function forceCloseOverlay() {
    clearTimer();
    clearFailsafe();
    if (pageEndTimer) {
      clearTimeout(pageEndTimer);
      pageEndTimer = null;
    }
    pageMode = false;
    state.value.open = false;
    state.value.progress = 0;
    state.value.steps = [];
    state.value.activeStep = 0;
  }

  function finish() {
    clearTimer();
    clearFailsafe();
    state.value.progress = 100;
    if (state.value.steps.length) {
      state.value.activeStep = state.value.steps.length - 1;
    }
    window.setTimeout(() => {
      state.value.open = false;
      state.value.progress = 0;
      state.value.steps = [];
      state.value.activeStep = 0;
      pageMode = false;
      if (resolveLock) {
        resolveLock();
        resolveLock = null;
      }
    }, 220);
  }

  /**
   * Show centered loading overlay, run `apply` near mid-progress, then finish.
   * Skips if already open (queues as immediate apply).
   */
  async function runSwitch(opts: {
    kind: SwitchOverlayKind;
    title: string;
    subtitle: string;
    apply: () => void | Promise<void>;
    steps?: SwitchOverlayStep[];
  }) {
    if (!import.meta.client) {
      await opts.apply();
      return;
    }
    if (state.value.open) {
      await opts.apply();
      return;
    }

    const steps = opts.steps || [];
    state.value = {
      open: true,
      kind: opts.kind,
      title: opts.title,
      subtitle: steps[0]?.label || opts.subtitle,
      progress: 0,
      steps,
      activeStep: 0,
    };

    const duration = MIN_MS + Math.floor(Math.random() * (MAX_MS - MIN_MS));
    const started = performance.now();
    let applied = false;

    await new Promise<void>((resolve) => {
      resolveLock = resolve;
      timer = setInterval(async () => {
        const elapsed = performance.now() - started;
        const p = Math.min(96, Math.round((elapsed / duration) * 100));
        state.value.progress = p;
        syncStepFromProgress(p, steps.length);

        if (!applied && elapsed >= duration * 0.38) {
          applied = true;
          try {
            await opts.apply();
          } catch {
            /* still finish overlay */
          }
        }

        if (elapsed >= duration) {
          finish();
        }
      }, 32);
    });
  }

  /** Start page-navigation / boot / auth loading (progress creeps toward 90% until endPageLoad). */
  function beginPageLoad(opts: {
    title: string;
    subtitle: string;
    kind?: SwitchOverlayKind;
    minMs?: number;
    steps?: SwitchOverlayStep[];
  }) {
    if (!import.meta.client) return;
    // locale/theme switch owns the overlay
    if (
      state.value.open &&
      !pageMode &&
      state.value.kind !== 'page' &&
      state.value.kind !== 'boot' &&
      state.value.kind !== 'auth'
    ) {
      return;
    }

    if (pageEndTimer) {
      clearTimeout(pageEndTimer);
      pageEndTimer = null;
    }
    clearFailsafe();

    const kind = opts.kind || 'page';
    const steps = opts.steps || [];
    pageMode = true;
    pageStartedAt = performance.now();
    pageMinMs =
      opts.minMs ??
      (kind === 'boot' ? BOOT_MIN_MS : kind === 'auth' ? AUTH_MIN_MS : PAGE_MIN_MS);
    clearTimer();

    state.value = {
      open: true,
      kind,
      title: opts.title,
      subtitle: steps[0]?.label || opts.subtitle,
      progress: kind === 'boot' ? 4 : kind === 'auth' ? 6 : 8,
      steps,
      activeStep: 0,
    };

    timer = setInterval(() => {
      if (!pageMode || !state.value.open) return;
      const elapsed = performance.now() - pageStartedAt;
      const base = kind === 'boot' ? 4 : kind === 'auth' ? 6 : 8;
      const target = Math.min(90, base + elapsed / (kind === 'auth' ? 18 : 16));
      const next = Math.max(state.value.progress, Math.round(target));
      if (next > state.value.progress) {
        state.value.progress = next;
        syncStepFromProgress(next, state.value.steps.length);
      }
    }, 40);

    // Jangan biarkan overlay nempel selamanya (bisa bikin "layar blank")
    pageFailsafeTimer = setTimeout(() => {
      if (state.value.open && pageMode) forceCloseOverlay();
    }, 8000);
  }

  /** Complete page-navigation loading (respects minimum visible time). */
  function endPageLoad() {
    if (!import.meta.client || !pageMode) return;
    pageMode = false;
    clearFailsafe();

    const elapsed = performance.now() - pageStartedAt;
    const wait = Math.max(0, pageMinMs - elapsed);

    const complete = () => {
      clearTimer();
      state.value.progress = 100;
      if (state.value.steps.length) {
        state.value.activeStep = state.value.steps.length - 1;
        const last = state.value.steps[state.value.steps.length - 1];
        if (last?.label) state.value.subtitle = last.label;
      }
      pageEndTimer = setTimeout(() => {
        state.value.open = false;
        state.value.progress = 0;
        state.value.steps = [];
        state.value.activeStep = 0;
        pageEndTimer = null;
      }, 260);
    };

    if (pageEndTimer) {
      clearTimeout(pageEndTimer);
      pageEndTimer = null;
    }

    if (wait > 0) {
      state.value.progress = Math.max(state.value.progress, 94);
      syncStepFromProgress(94, state.value.steps.length);
      pageEndTimer = setTimeout(complete, wait);
    } else {
      complete();
    }
  }

  return { state, runSwitch, beginPageLoad, endPageLoad, forceCloseOverlay };
}
